/**
 * TodayCard.jsx — Feb 09 2026, Lane 2 (Subscriber Home restructure).
 *
 * Signed-in Home landing card #1. Shows today's scheduled planner
 * lessons (title, subject, period/time). Renders ONLY for Pro / Pro+
 * teachers who actually have Planner access — Basic / Standard tiers
 * skip this card entirely (their Home starts at Skill in the Wild).
 *
 * Data source: `GET /api/planner/events?start=YYYY-MM-DD&end=YYYY-MM-DD`
 * with start = end = today (client-side UTC). Filters kind==="lesson"
 * so non-lesson blocks (holidays, prep, etc.) do not appear.
 *
 * Empty states:
 *   - School year (Sept 1 – Jun 30): "Nothing scheduled today — open your Planner →"
 *   - Summer (Jul 1 – Aug 31, same date gate as SITW summer branch):
 *     "No classes — it's summer. Jump back into recent work below, or
 *     plan ahead for September →"
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock } from "lucide-react";
import { api } from "../../lib/api";
import { upcomingSeptemberSchoolYear } from "../../lib/lrp";
import { naturalCompare } from "../../lib/naturalCompare";

const _todayISO = () => {
  const t = new Date();
  const yyyy = t.getFullYear();
  const mm = String(t.getMonth() + 1).padStart(2, "0");
  const dd = String(t.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const _isSummer = (date = new Date()) => {
  const m = date.getMonth() + 1; // 1-12
  return m === 7 || m === 8;
};

const _isProUser = (user) => {
  if (!user) return false;
  if (user.has_math_engine_access) return true;
  // Jun 2026 — the Today card now shows for EVERY active subscriber
  // (Basic / Standard / Pro / Pro+), not just Pro. Non-subscribers
  // (anonymous / free) still hide it. The planner-events call returns
  // an empty list for teachers without a schedule, which renders the
  // "Nothing scheduled — open your Planner" soft-upsell state.
  return !!user.subscription?.is_active;
};

export default function TodayCard({ user }) {
  const [events, setEvents] = useState(null); // null = loading, [] = empty
  const [errored, setErrored] = useState(false);
  const isPro = _isProUser(user);
  const summer = _isSummer();

  useEffect(() => {
    if (!isPro) return;
    let alive = true;
    const today = _todayISO();
    api
      .get("/planner/events", { params: { start: today, end: today } })
      .then((r) => {
        if (!alive) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        // Only lessons — non-lesson blocks (holidays, prep, etc.) skip.
        const lessons = rows.filter((e) => (e.kind || "lesson") === "lesson");
        // Sort by period_label using natural order so "Period 10"
        // lands after "Period 9" instead of after "Period 1" (Lane 2
        // follow-up — the founder's "period natural-sort" rider).
        // Undefined labels sort to the end.
        lessons.sort((a, b) => {
          const pa = a.period_label || "\uffff";
          const pb = b.period_label || "\uffff";
          return naturalCompare(pa, pb);
        });
        setEvents(lessons);
      })
      .catch(() => {
        if (!alive) return;
        setErrored(true);
        setEvents([]);
      });
    return () => {
      alive = false;
    };
  }, [isPro]);

  // Non-Pro subscribers: card hides entirely (spec 1 / decision (b)).
  if (!isPro) return null;

  return (
    <section data-testid="home-today-card" className="ae-card p-6 md:p-7">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-[#f4f5f7] border border-[#4a6984]/15 grid place-items-center">
            <CalendarDays className="h-4 w-4 text-[#4a6984]" />
          </div>
          <span className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
            Today
          </span>
        </div>
        {events && events.length > 0 && (
          <span className="ae-pill" data-testid="home-today-count">
            {events.length} scheduled
          </span>
        )}
      </div>

      {events === null && !errored && (
        <div data-testid="home-today-loading" className="space-y-2.5">
          <div className="h-3 rounded bg-[#4a6984]/10 animate-pulse w-9/12" />
          <div className="h-3 rounded bg-[#4a6984]/10 animate-pulse w-6/12" />
        </div>
      )}

      {events !== null && events.length === 0 && (
        <div data-testid="home-today-empty" className="text-sm text-[#4a6984]/85 font-medium leading-relaxed">
          {summer ? (
            <>
              No classes — it&apos;s summer. Jump back into recent work below, or{" "}
              <Link
                to={`/long-range-plan?school_year=${upcomingSeptemberSchoolYear()}`}
                data-testid="home-today-empty-summer-cta"
                className="inline-flex items-center gap-1 font-bold text-[#1c2d3d] hover:text-[#4a6984] transition-colors"
              >
                plan ahead for September <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </>
          ) : (
            <Link
              to="/?view=planner"
              data-testid="home-today-empty-cta"
              className="inline-flex items-center gap-1 font-semibold text-[#1c2d3d] hover:text-[#4a6984] transition-colors"
            >
              Nothing scheduled today — open your Planner
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      )}

      {events !== null && events.length > 0 && (
        <ul data-testid="home-today-list" className="space-y-2">
          {events.map((ev) => {
            const href = ev.activity_id
              ? `/lesson/${encodeURIComponent(ev.activity_id)}`
              : "/?view=planner";
            const inner = (
              <>
                <div className="flex items-center gap-3 min-w-0">
                  {ev.period_label && (
                    <span
                      data-testid={`home-today-period-${ev.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#4a6984]/70 shrink-0"
                    >
                      <Clock className="h-3 w-3" />
                      {ev.period_label}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-extrabold text-sm text-[#1c2d3d] truncate">
                      {ev.activity_name || ev.subject || "Untitled block"}
                    </p>
                    {ev.subject && ev.activity_name && (
                      <p className="text-xs text-[#4a6984]/70 truncate">{ev.subject}</p>
                    )}
                  </div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-[#4a6984]/40 group-hover:text-[#1c2d3d] transition-colors shrink-0" />
              </>
            );
            return (
              <li key={ev.id}>
                <Link
                  to={href}
                  data-testid={`home-today-item-${ev.id}`}
                  className="group flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-[#4a6984]/10 hover:border-[#1c2d3d]/30 hover:bg-[#f4f5f7]/60 transition"
                >
                  {inner}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
